import React, { useState } from 'react';
import { useCurrentAccount, useSignPersonalMessage, ConnectButton,
         useAccounts, useSwitchAccount, useCurrentWallet, useDisconnectWallet } from '@mysten/dapp-kit';
import { KeyRound, ShieldCheck, ShieldOff, RefreshCw } from 'lucide-react';
import { useWS } from '../WSContext';

// Bluefin Pro — hesap yetkilendirme.
//
// Neden gerekli: LP cüzdanı zkLogin (Slush/Google) olduğu için dışa aktarılabilir anahtarı
// yok; backend onun adına imzalayamıyor ve trade/funding geçmişini çekemiyor. Bluefin
// başka bir adresi hesap adına yetkilendirmeye izin veriyor (arayüzün "1CT" anahtarları
// da aynı mekanizma). Burada backend'in ürettiği adresi bir kez yetkilendiriyoruz.
//
// Zincire hiçbir şey yazılmaz, gas yoktur: yalnızca signPersonalMessage ile imzalanmış
// bir JSON gateway'e PUT edilir.
//
// HTTP'yi neden backend atıyor: Bluefin gateway'i CORS'u origin bazında beyaz listeliyor
// (trade.bluefin.io geçiyor, barbarosquant.org geçmiyor) → tarayıcıdan doğrudan çağrı
// "Failed to fetch" veriyordu. İmza yalnız cüzdanda üretilebilir, HTTP her yerden atılır:
// imzayı burada bırakıp isteği WS üzerinden backend'e yaptırıyoruz. İmzalanacak METNİ de
// backend kuruyor — JS/Python JSON biçimlendirmesi ayrışırsa imza sessizce geçersiz olurdu.
//
// UYARI: yetkinin kapsam alanı yok — verilen adres teknik olarak işlem de yapabilir.
// Biz yalnız okuma için kullanıyoruz (backend readOnly token istiyor) ve yetki
// istenildiği an aşağıdaki butonla geri alınabilir.

const ALIAS = 'lptrax-reader';
const PAYLOAD_TYPE = 'Bluefin Pro Authorize Account';

type Wallet = { address: string; alias: string; authorizedAtMillis: number };

export default function BluefinAuthorize({ dark = true, account }: { dark?: boolean; account: string }) {
  const current = useCurrentAccount();
  const accounts = useAccounts();
  const { currentWallet } = useCurrentWallet();
  const { mutate: switchAccount } = useSwitchAccount();
  const { mutate: disconnect } = useDisconnectWallet();
  const { mutateAsync: signPersonalMessage } = useSignPersonalMessage();
  const { sendWs, subscribe } = useWS() as any;
  const eq = (a?: string) => (a || '').toLowerCase() === account.toLowerCase();
  const target = accounts.find(a => eq(a.address));   // hedef hesap cüzdanda var mı
  const [reader, setReader]   = useState('');
  const [busy, setBusy]       = useState('');
  const [msg, setMsg]         = useState<{ ok: boolean; text: string } | null>(null);
  const [list, setList]       = useState<Wallet[] | null>(null);

  const card  = dark ? 'bg-[#061d3f] border-[#0a2560]' : 'bg-white border-[#d4e0f0]';
  const sub   = dark ? 'bg-[#03112b]/60 border-[#0a2560]' : 'bg-[#eef3fa] border-[#d4e0f0]';
  const muted = dark ? 'text-blue-300/70' : 'text-slate-500';
  const input = dark
    ? 'bg-[#03112b] border-[#0a2560] text-blue-50 placeholder-blue-300/40'
    : 'bg-white border-[#d4e0f0] text-slate-800 placeholder-slate-400';

  // Yetkili adresler KİMLİKSİZ uçtan okunabiliyor → doğrulama bedava.
  const refresh = () => {
    setBusy('list'); setMsg(null);
    sendWs?.({ type: 'bluefin_auth_list', payload: { account } });
  };

  // Backend yanıtları
  React.useEffect(() => {
    const unsub = subscribe?.((m: any) => {
      if (m.type === 'bluefin_auth_wallets') {
        setBusy('');
        if (m.payload?.success) setList(m.payload.authorizedWallets ?? []);
        else setMsg({ ok: false, text: `Liste okunamadı: ${m.payload?.error}` });
      } else if (m.type === 'bluefin_auth_prepared') {
        if (!m.payload?.success) { setBusy(''); setMsg({ ok: false, text: m.payload?.error || 'hazırlık hatası' }); return; }
        // Backend'in ürettiği metni AYNEN imzala
        signPersonalMessage({ message: new TextEncoder().encode(m.payload.message) })
          .then(({ signature }: any) => sendWs?.({ type: 'bluefin_auth_submit', payload: {
            signedFields: m.payload.signedFields, signature, alias: ALIAS, status: m.payload.status } }))
          .catch((e: any) => { setBusy(''); setMsg({ ok: false, text: e.message || 'imza iptal edildi' }); });
      } else if (m.type === 'bluefin_auth_result') {
        setBusy('');
        const p = m.payload || {};
        if (p.success) { setMsg({ ok: true, text: 'İşlem başarılı. Liste tazeleniyor…' }); refresh(); }
        else setMsg({ ok: false, text: p.error || `HTTP ${p.httpStatus} — ${p.body || ''}` });
      }
    });
    return () => unsub?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe, sendWs, account]);

  const send = (status: boolean) => {
    const user = reader.trim().toLowerCase();
    if (!/^0x[0-9a-f]{64}$/.test(user)) { setMsg({ ok: false, text: 'Adres 0x + 64 hex karakter olmalı.' }); return; }
    if (!current) { setMsg({ ok: false, text: 'Önce cüzdanı bağla.' }); return; }
    if (!eq(current.address)) {
      setMsg({ ok: false, text: `Bağlı cüzdan ${current.address.slice(0,10)}… — hedef ${account.slice(0,10)}… değil.` });
      return;
    }
    setBusy(status ? 'auth' : 'deauth'); setMsg(null);
    sendWs?.({ type: 'bluefin_auth_prepare', payload: { account, user, status } });
  };

  return (
    <div className={`rounded-xl border ${card} p-3 mb-4 text-xs`}>
      <div className="flex items-center gap-2 mb-2">
        <KeyRound size={14} className="text-amber-400" />
        <span className="font-semibold text-sm">Bluefin okuyucu yetkisi</span>
        <span className={muted}>hesap {account.slice(0, 8)}…{account.slice(-4)}</span>
        <button onClick={refresh} disabled={!!busy}
          className={`ml-auto px-2.5 py-1 rounded-md border ${sub} ${muted} hover:text-sky-400 disabled:opacity-40 flex items-center gap-1`}>
          <RefreshCw size={11} className={busy === 'list' ? 'animate-spin' : ''} /> Yetkilileri getir
        </button>
      </div>

      <p className={`${muted} mb-2 leading-relaxed`}>
        Backend'in ürettiği adresi bu hesap adına yetkilendirir; zkLogin cüzdanı dışa aktarılabilir
        anahtarı olmadığı için tek yol bu. Zincire yazılmaz, gas yoktur — tek bir imza.
        Yetkinin kapsamı yok: verilen adres teknik olarak işlem de yapabilir, biz yalnız okuma için
        kullanıyoruz ve istediğin an geri alabilirsin.
      </p>

      {/* Bağlantı durumu — "hedef hesap değil" uyarısı tek başına çıkmazdı, hangi hesabın
          bağlı olduğunu ve geçişin mümkün olup olmadığını da göstermek gerekiyor. */}
      <div className={`rounded-lg border ${sub} p-2 mb-2 flex items-center gap-2 flex-wrap`}>
        <span className={muted}>Bağlı:</span>
        {current ? (
          <span className={`font-mono text-[11px] ${eq(current.address) ? 'text-emerald-400' : 'text-amber-400'}`}>
            {current.address.slice(0, 10)}…{current.address.slice(-6)}
            {currentWallet?.name ? <span className={`ml-1.5 ${muted}`}>({currentWallet.name})</span> : null}
          </span>
        ) : <span className={muted}>cüzdan bağlı değil</span>}

        {current && eq(current.address) && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
            hedef hesap ✓
          </span>
        )}

        <div className="ml-auto flex items-center gap-2">
          {!current && <ConnectButton />}
          {current && !eq(current.address) && target && (
            <button onClick={() => switchAccount({ account: target })}
              className="px-2.5 py-1 rounded-md border border-sky-500/40 bg-sky-500/15 text-sky-300 hover:bg-sky-500/25">
              Hedef hesaba geç
            </button>
          )}
          {current && !eq(current.address) && !target && (
            <button onClick={() => disconnect()}
              className={`px-2.5 py-1 rounded-md border ${sub} ${muted} hover:text-rose-400`}>
              Bağlantıyı kes ve yeniden bağlan
            </button>
          )}
        </div>
      </div>

      {current && !eq(current.address) && !target && (
        <p className="text-amber-400 mb-2 leading-relaxed">
          Bu cüzdanda hedef hesap görünmüyor{accounts.length > 1 ? ` (${accounts.length} hesap açık)` : ''}.
          Slush'ta <span className="font-mono">{account.slice(0, 10)}…{account.slice(-6)}</span> hesabına
          geçip yeniden bağlan — Slush'ta hesap değiştirmek uygulamaya otomatik yansımayabiliyor.
        </p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <input value={reader} onChange={e => setReader(e.target.value)}
          placeholder="yetkilendirilecek adres (0x…)"
          className={`flex-1 min-w-[320px] font-mono text-[11px] px-2.5 py-1.5 rounded-lg border ${input} focus:outline-none focus:border-sky-500/60`} />
        <button onClick={() => send(true)} disabled={!!busy || !current}
          className="px-3 py-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 disabled:opacity-40 flex items-center gap-1.5">
          <ShieldCheck size={12} /> {busy === 'auth' ? 'İmzalanıyor…' : 'Yetkilendir'}
        </button>
        <button onClick={() => send(false)} disabled={!!busy || !current}
          className="px-3 py-1.5 rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 disabled:opacity-40 flex items-center gap-1.5">
          <ShieldOff size={12} /> {busy === 'deauth' ? 'İmzalanıyor…' : 'Yetkiyi kaldır'}
        </button>
      </div>

      {msg && (
        <p className={`mt-2 break-words ${msg.ok ? 'text-emerald-400' : 'text-rose-400'}`}>{msg.text}</p>
      )}

      {list && (
        <div className={`mt-2 rounded-lg border ${sub} p-2`}>
          <div className={`${muted} mb-1`}>Yetkili adresler ({list.length})</div>
          {list.length === 0 && <div className={muted}>—</div>}
          {list.map(w => (
            <div key={w.address} className="flex items-center gap-2 font-mono text-[11px] py-0.5">
              <span className={`px-1.5 py-0.5 rounded text-[10px] ${w.alias === ALIAS
                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                : `${muted} border`}`}>{w.alias || '—'}</span>
              <span>{w.address.slice(0, 10)}…{w.address.slice(-6)}</span>
              <span className={`ml-auto ${muted}`}>
                {w.authorizedAtMillis ? new Date(w.authorizedAtMillis).toLocaleDateString('tr-TR') : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
